import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { ToastProvider } from './components/Toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { isAdminRole } from './utils/authRole';
import './index.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const TraditionalPage = lazy(() => import('./pages/ShopPages').then(m => ({ default: m.TraditionalPage })));
const ModernPage = lazy(() => import('./pages/ShopPages').then(m => ({ default: m.ModernPage })));

const ProductPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.ProductPage })));
const LoginPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.RegisterPage })));
const AccountPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.AccountPage })));
const OrdersPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.OrdersPage })));
const WishlistPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.WishlistPage })));
const CheckoutPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.CheckoutPage })));
const CollectionsPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.CollectionsPage })));
const AboutPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.AboutPage })));
const AdminPage = lazy(() => import('./pages/AllPages').then(m => ({ default: m.AdminPage })));
const AdminProductForm = lazy(() => import('./pages/AllPages').then(m => ({ default: m.AdminProductForm })));

const PrivacyPolicyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const ContactPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.ContactPage })));

function Guard({ children, adminOnly = false }) {
  const { user } = useApp();
  const isAdmin = isAdminRole(user);
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return children;
}

function Layout() {
  return (
    <>
      <Navbar />
      <main>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/"             element={<HomePage />} />
            <Route path="/traditional"  element={<TraditionalPage />} />
            <Route path="/modern"       element={<ModernPage />} />
            <Route path="/shop"         element={<Navigate to="/traditional" replace />} />
            <Route path="/product/:id"  element={<ProductPage />} />
            <Route path="/collections"  element={<CollectionsPage />} />
            <Route path="/about"        element={<AboutPage />} />
            <Route path="/login"        element={<LoginPage />} />
            <Route path="/register"     element={<RegisterPage />} />
            <Route path="/account"      element={<Guard><AccountPage /></Guard>} />
            <Route path="/orders"       element={<Guard><OrdersPage /></Guard>} />
            <Route path="/wishlist"     element={<Guard><WishlistPage /></Guard>} />
            <Route path="/checkout"     element={<Guard><CheckoutPage /></Guard>} />
            <Route path="/admin"        element={<Guard adminOnly><AdminPage /></Guard>} />
            <Route path="/admin/products/new"      element={<Guard adminOnly><AdminProductForm /></Guard>} />
            <Route path="/admin/products/:id/edit" element={<Guard adminOnly><AdminProductForm /></Guard>} />
            <Route path="/privacy-policy"   element={<PrivacyPolicyPage />} />
            <Route path="/terms"            element={<TermsPage />} />
            <Route path="/contact"          element={<ContactPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <ToastProvider>
          <Layout />
        </ToastProvider>
      </AppProvider>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight:'100vh', background:'var(--ink)', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:20, textAlign:'center' }}>
      <div style={{ fontFamily:'var(--font-serif)', fontSize:140, color:'rgba(184,147,58,0.1)', lineHeight:1 }}>404</div>
      <h2 style={{ fontFamily:'var(--font-serif)', fontSize:36, color:'var(--cream)', fontWeight:400, marginTop:-60 }}>Page not found</h2>
      <p style={{ fontSize:14, color:'rgba(240,232,210,0.4)' }}>The page you're looking for doesn't exist.</p>
      <a href="/" className="btn btn-gold" style={{ marginTop:16, padding:'14px 40px', textDecoration:'none', display:'inline-flex' }}>Back to Home</a>
    </div>
  );
}

function RouteLoader() {
  return (
    <div style={{ minHeight: '52vh', display: 'grid', placeItems: 'center' }}>
      <div className="spinner" />
    </div>
  );
}